import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import axios from "axios";
import toast from "react-hot-toast";
import { logout } from "./authSlice";

// Async thunk for fetching navigation items
export const fetchNavItems = createAsyncThunk(
  "navigation/fetchNavItems",
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      // Get token from auth state
      const token = getState().auth.token;

      const response = await axios.get(
        `${import.meta.env.VITE_BASE_SERVER_URL}api/glabp/?page=get_root`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 504) {
        dispatch(logout());
      }
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch navigation items" }
      );
    }
  }
);

const initialState = {
  navItems: [],
  userInformation: null,
  activeItem: null,
  expandedMenus: [],
  loading: false,
  error: null,
};

const navigationSlice = createSlice({
  name: "navigation",
  initialState,
  reducers: {
    setActiveItem: (state, { payload }) => {
      state.activeItem = payload;
    },
    toggleSubmenu: (state, { payload }) => {
      // If menu is already expanded, collapse it
      if (state.expandedMenus.includes(payload)) {
        state.expandedMenus = state.expandedMenus.filter(
          (id) => id !== payload
        );
      } else {
        // Otherwise expand it
        state.expandedMenus.push(payload);
      }
      localStorage.setItem(
        "expandedMenus",
        JSON.stringify(state.expandedMenus)
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNavItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNavItems.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.navItems = payload;
        // set the user information that's coming from the server
        state.userInformation = payload.find((item) => item.username);

        // Auto-expand menus marked as "open_sub_menu" with show state
        const expandedIds = [];
        payload.forEach((item) => {
          if (item.action === "open_sub_menu" && item.state === "show") {
            expandedIds.push(item.glid);
          }
        });
        // state.expandedMenus = expandedIds;

        // state.expandedMenus = []; // Force everything collapsed by default
      })
      .addCase(fetchNavItems.rejected, (state, { payload }) => {
        if ("error" in payload && payload.error?.status === 504) {
          toast.error(
            payload?.error.data?.[0] || "Session expired, please login again",
            {
              duration: 5000,
            }
          );
        }
        state.loading = false;
        state.error = payload;
      });
  },
});

// Memoized selectors
const selectNavItems = (state) => state.navigation.navItems;
const selectExpandedMenus = (state) => state.navigation.expandedMenus;

// Memoized helper selector
export const selectVisibleNavItems = createSelector(
  [selectNavItems, selectExpandedMenus],
  (navItems, expandedMenus) => {
    // Filter items based on user role and status
    return navItems.filter((item) => {
      // Item with 'hide' state should not be shown
      if (item.state === "hide") return false;

      // For submenu items, only show if their parent menu is expanded
      if (item.level > 0) {
        // Find parent item by looking at all items with children
        const parentItem = navItems.find(
          (parent) => parent.children && parent.children.includes(item.glid)
        );

        if (parentItem && !expandedMenus.includes(parentItem.glid)) {
          return false;
        }
      }

      return true;
    });
  }
);

// Helper for checking if a menu is expanded
export const selectIsMenuExpanded = (state, menuId) => {
  return state.navigation.expandedMenus.includes(menuId);
};

// Helper to get item enabled state
export const selectIsItemEnabled = (item) => {
  return item.state !== "disable";
};

// Helper to determine what happens when an item is clicked
export const handleNavItemClick = (item, dispatch, navigate) => {
  if (!selectIsItemEnabled(item)) return; // Do nothing if disabled

  switch (item.action) {
    case "goto_url":
      window.open(item.action_arg, "_blank");
      break;
    case "goto_page":
      navigate(item.action_arg);
      break;
    case "open_sub_menu":
      dispatch(toggleSubmenu(item.glid));
      break;
    case "seperator":
      // Separators don't have click actions
      break;
    default:
      // No action or unrecognized action
      break;
  }
};

export const { setActiveItem, toggleSubmenu } = navigationSlice.actions;

export default navigationSlice.reducer;
